import { Type, Transform } from 'class-transformer';
import {
  IsInt,
  IsNumber,
  IsIn,
  IsArray,
  IsString,
  IsDateString,
  IsUUID,
  IsNotEmpty,
  ValidateNested,
  IsOptional,
  IsBoolean,
} from 'class-validator';
import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import {
  FiltersResponse,
  Filter,
  FilterService,
  FilterType,
} from '@vk-hr-tek/core/filter';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { QueryService } from '@vk-hr-tek/core/query';

export class GetUserBellCounterQuery {
  @IsBoolean()
  @IsOptional()
  is_read?: boolean;
}

export class GetUserBellQuery {
  @IsUUID()
  @IsOptional()
  last_id?: string;

  @IsInt()
  @IsOptional()
  limit?: number;

  @IsBoolean()
  @IsOptional()
  is_read?: boolean;
}

export class BellMarkReadAllResponse {
  @IsInt()
  @IsNotEmpty()
  marked_count: number;
}

export class BellCounterResponse {
  @IsInt()
  @IsNotEmpty()
  bell_notification_counter: number;
}

export type Slug = string;

export class BellNotificationPayload {
  @IsUUID()
  @IsOptional()
  news_id?: string;

  @IsUUID()
  @IsOptional()
  vote_id?: string;

  @IsUUID()
  @IsOptional()
  comment_id?: string;

  @IsUUID()
  @IsOptional()
  survey_id?: string;

  @IsUUID()
  @IsOptional()
  community_id?: string;

  @IsString()
  @IsOptional()
  slug?: string;

  @IsUUID()
  @IsOptional()
  post_id?: string;
}

export class BellNotification {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  notifiable_name: string;

  @IsIn([
    'news',
    'comment',
    'survey',
    'community',
    'community_manage',
    'community_post',
    'community_post_comment',
    'mention',
  ])
  @IsString()
  @IsNotEmpty()
  notifiable_nick:
    | 'news'
    | 'comment'
    | 'survey'
    | 'community'
    | 'community_manage'
    | 'community_post'
    | 'community_post_comment'
    | 'mention';

  @IsString()
  @IsNotEmpty()
  action_name: string;

  @IsIn([
    'create_news',
    'survey_start',
    'survey_remind',
    'survey_end',
    'community_invite',
    'community_remove',
    'community_invite_accept',
    'community_invite_reject',
    'community_application',
    'community_application_accept',
    'community_application_reject',
    'community_new_comment',
    'community_comment_reply',
    'community_new_post',
    'community_post_mention',
    'community_comment_mention',
    'community_new_administrator',
    'community_new_moderator',
    'community_new_editor',
    'community_new_owner',
    'community_owner_remove',
    'community_new_member',
    'community_post_suggest',
    'community_post_publication',
    'community_post_reject',
    'community_post_delete',
    'community_moderation_post_delete',
  ])
  @IsString()
  @IsNotEmpty()
  action_nick:
    | 'create_news'
    | 'survey_start'
    | 'survey_remind'
    | 'survey_end'
    | 'community_invite'
    | 'community_remove'
    | 'community_invite_accept'
    | 'community_invite_reject'
    | 'community_application'
    | 'community_application_accept'
    | 'community_application_reject'
    | 'community_new_comment'
    | 'community_comment_reply'
    | 'community_new_post'
    | 'community_post_mention'
    | 'community_comment_mention'
    | 'community_new_administrator'
    | 'community_new_moderator'
    | 'community_new_editor'
    | 'community_new_owner'
    | 'community_owner_remove'
    | 'community_new_member'
    | 'community_post_suggest'
    | 'community_post_publication'
    | 'community_post_reject'
    | 'community_post_delete'
    | 'community_moderation_post_delete';

  @IsString()
  @IsNotEmpty()
  message: string;

  @IsDateString()
  @IsNotEmpty()
  created_at: string;

  @IsDateString()
  @IsOptional()
  read_at?: string;

  @ValidateNested()
  @Type(() => BellNotificationPayload)
  @IsOptional()
  payload?: BellNotificationPayload;
}

export class BellNotificationResponse {
  @ValidateNested({ each: true })
  @Type(() => BellNotification)
  @IsArray()
  @IsNotEmpty()
  bell_notifications: BellNotification[];
}

export class BellMarkReadRequest {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  notification_ids: string[];
}

@injectable()
export class BellService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private query: QueryService,
  ) {}

  async bellNotificationMarkReadAll() {
    let requestPath = '/bell/mark_read_all';

    const result = await this.http.post(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      BellMarkReadAllResponse,
    );

    return response;
  }

  async bellNotificationMarkRead({ body }: { body: BellMarkReadRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, BellMarkReadRequest),
    ]);

    let requestPath = '/bell/mark_read';

    const result = await this.http.post(requestPath, body, {});

    return result;
  }

  async getUserBellCounter({
    query = {},
  }: {
    query?: GetUserBellCounterQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetUserBellCounterQuery),
    ]);

    let requestPath = '/bell/counter';

    const result = await this.http.get(requestPath, query, {
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      BellCounterResponse,
    );

    return response;
  }

  async getUserBell({
    query = {},
  }: {
    query?: GetUserBellQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetUserBellQuery),
    ]);

    let requestPath = '/bell';

    const result = await this.http.get(requestPath, query, {
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      BellNotificationResponse,
    );

    return response;
  }
}
